import React from 'react';
import { CaretDown, CheckSquare } from '@phosphor-icons/react';
import { alpha, Palette, Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: PaletteColor;
    low: {
      main: string;
    };
    high: {
      main: string;
    };
  }
  interface PaletteOptions {
    neutral: PaletteColor;
    low: {
      main: string;
    };
    high: {
      main: string;
    };
  }

  interface PaletteColor {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
  }
  interface SimplePaletteColorOptions {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
  }
}

const defaultPalette = {
  primary: {
    50: '#FFF2F7',
    100: '#FFE5F0',
    200: '#FFCCE5',
    300: '#FF99CC',
    400: '#FF66B3',
    500: '#ff3399',
    600: '#E62B89',
    700: '#CC267A',
    light: '#FFCCE5',
    main: '#FF3399',
    dark: '#CC267A',
    contrastText: '#fff',
  },
  secondary: {
    50: '#EEF5FF',
    100: '#D6E7FF',
    200: '#B3D1FF',
    300: '#8AB4FF',
    400: '#5B97FF',
    500: '#3E80FF',
    600: '#3571E6',
    700: '#2C60CC',
    light: '#B3D1FF',
    main: '#3E80FF',
    dark: '#2C60CC',
    contrastText: '#fff',
  },
  error: {
    50: '#FFF2F0',
    100: '#FFE1E1',
    200: '#FFCCC7',
    300: '#FFA39E',
    400: '#FF7875',
    500: '#FF4D4F',
    600: '#D43C3E',
    700: '#B83234',
    light: '#FFCCC7',
    main: '#FF4D4F',
    dark: '#B83234',
    contrastText: '#fff',
  },
  warning: {
    50: '#FFF9EB',
    100: '#FFF0CC',
    200: '#FFE0A8',
    300: '#FFCD7A',
    400: '#FFB957',
    500: '#FFA63A',
    600: '#E69533',
    700: '#CC842E',
    light: '#FFE0A8',
    main: '#FFA63A',
    dark: '#CC842E',
    contrastText: '#fff',
  },
  success: {
    50: '#F3FBF6',
    100: '#DDF5E6',
    200: '#BEEBD1',
    300: '#98DBB8',
    400: '#6DCA9B',
    500: '#47B881',
    600: '#3DA36D',
    700: '#2F865B',
    light: '#BEEBD1',
    main: '#47B881',
    dark: '#2F865B',
    contrastText: '#fff',
  },
  neutral: {
    50: '#FFFFFF',
    100: '#F5F5F5',
    200: '#E0E0E0',
    300: '#CCCCCC',
    400: '#A3A3A3',
    500: '#666666',
    600: '#404040',
    700: '#111213',
    light: '#F5F5F5',
    main: '#666666',
    dark: '#111213',
    contrastText: '#000',
  },
  low: {
    main: '#000000',
  },
  high: {
    main: '#FFFFFF',
  },
  text: {
    primary: '#000000',
    secondary: '#666666',
  },
  action: {
    disabledBackground: '#F5F5F5',
    disabled: '#CCCCCC',
  },
} as Palette;

const typography = {
  fontFamily: [
    'Poppins',
    'Helvetica',
    'Arial',
    'sans-serif',
  ].join(','),
  fontWeightMedium: 600,
  h2: {
    fontSize: '3rem',
  },
  h3: {
    fontSize: '2.5rem',
    lineHeight: 2,
  },
  h4: {
    fontSize: '2rem',
    lineHeight: 1.7,
  },
  h5: {
    fontSize: '1.5rem',
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.25rem',
    lineHeight: 1.33,
  },
  subtitle1: {
    fontSize: '0.875rem',
    lineHeight: 1.15,
  },
  subtitle2: {
    fontSize: '0.75rem',
    lineHeight: 1,
  },
} as Theme['typography'];

const defaultTheme = createTheme();

const shadows = [
  'none',
  '0px 4px 8px rgba(0, 0, 0, 0.16)',
  '0px 8px 24px rgba(56, 16, 16, 0.16)',
  '0px 16px 32px rgba(0, 0, 0, 0.12)',
  '0px 16px 48px rgba(0, 0, 0, 0.24)',
  ...defaultTheme.shadows,
] as unknown as Theme['shadows'];

export default (
  palette = defaultPalette,
):Theme => ({
  ...defaultTheme,
  palette,
  typography,
  shadows,
  components: {
    MuiButton: {
      defaultProps: {
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.16)',
          borderRadius: '50px',
          textTransform: 'none',
          transition: 'color .01s',
          fontWeight: 500,
          '&:hover': {
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: palette.neutral[200],
            boxShadow: 'none',
            color: palette.neutral[400],
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: palette.primary[400],
          },
          '&:active': {
            backgroundColor: palette.primary[300],
          },
        },
        containedSecondary: {
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: palette.secondary[400],
          },
          '&:active': {
            backgroundColor: palette.secondary[300],
          },
        },
        containedWarning: {
          color: palette.low.main,
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: palette.warning[400],
          },
          '&:active': {
            backgroundColor: palette.warning[200],
          },
        },
        outlined: {
          backgroundColor: palette.high.main,
          boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.16)',
          '&:hover': {
            backgroundColor: palette.neutral[200],
          },
          '&:active': {
            backgroundColor: palette.neutral[400],
          },
        },
        outlinedPrimary: {
          '&:hover': {
            backgroundColor: palette.primary[50],
          },
          '&:active': {
            backgroundColor: palette.primary[100],
          },
        },
        outlinedSecondary: {
          '&:hover': {
            backgroundColor: palette.secondary[50],
          },
          '&:active': {
            backgroundColor: palette.secondary[100],
          },
        },
        outlinedWarning: {
          '&:hover': {
            backgroundColor: palette.warning[50],
          },
          '&:active': {
            backgroundColor: palette.warning[100],
          },
        },
        outlinedError: {
          '&:hover': {
            backgroundColor: palette.error[50],
          },
          '&:active': {
            backgroundColor: palette.error[100],
          },
        },
        text: {
          boxShadow: 'none',
          textDecoration: 'underline',
          margin: 0,
          padding: 0,
          '&:hover': {
            color: palette.neutral[600],
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          },
          '&:active': {
            color: palette.neutral[400],
            backgroundColor: 'transparent',
          },
          '&:disabled': {
            color: palette.neutral[300],
            backgroundColor: 'transparent',
          },
        },
        sizeLarge: {
          padding: '12px 24px',
          lineHeight: 1.2,
          height: 48,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 28,
          boxShadow: `0px 3px 6px ${alpha(palette.low.main, 0.16)}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: shadows[2],
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          alignItems: 'center',
          transition: 'border 0.3s ease',
          color: palette.neutral[700],
          fontSize: 14,
          fontWeight: 500,
          '&.Mui-focused': {
            border: `solid 1px ${palette.primary[500]}`,
          },
        },
        underline: {
          '&:after': {
            display: 'none',
          },
          '&:before': {
            display: 'none',
          },
        },
        colorSecondary: {
          '&.Mui-focused': {
            border: `solid 1px ${palette.secondary[500]}`,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: 48,
          color: palette.neutral[700],
          fontSize: 14,
          fontWeight: 500,
          borderWidth: 1,
          overflow: 'hidden !important',
          border: `1px solid ${palette.neutral[500]}`,
          '&.Mui-error': {
            borderColor: `${palette.error[500]} !important`,
            backgroundColor: palette.error[200],
            overflow: 'hidden',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${palette.error[500]} !important`,
            },
          },
        },
        formControl: {
          'label + &': {
            marginTop: 16,
          },
        },
        input: {
          padding: '14px 16px',
          '&:disabled': {
            backgroundColor: alpha(palette.low.main, 0.05),
            borderRadius: 50,
            color: alpha(palette.low.main, 0.5),
          },
          '&:read-only': {
            backgroundColor: alpha(palette.low.main, 0.05),
            borderRadius: 50,
            color: alpha(palette.low.main, 0.5),
          },
        },
      },
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.neutral[500],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 0,
          },
          '& fieldset': {
            border: 'none',
          },
          '&.Mui-focused': {
            border: `solid 1px ${palette.primary[500]}`,
          },
        },
        marginDense: {
          borderRadius: 24,
        },
        colorSecondary: {
          '&.Mui-focused': {
            border: `solid 1px ${palette.secondary[500]}`,
          },
        },
      },
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          '&:before': {
            flex: 0,
            padding: 0,
            display: 'none',
          },
        },
      },
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 28,
          padding: 24,
          boxShadow: `0px 3px 6px ${alpha(palette.low.main, 0.15)}`,
          outline: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingBottom: 24,
          borderBottom: '1px solid #DFE2EC',
          marginBottom: 24,
          fontSize: '1.25rem',
          fontWeight: 'bold',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.low.main, 0.35),
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(17, 17, 17, 0.7)',
          fontSize: 14,
          fontWeight: 500,
          '&$error': {
            zIndex: 1,
          },
        },
        formControl: {
          transform: 'translate(16px, 30px) scale(1)',
          fontSize: 14,
        },
        shrink: {
          transform: 'translate(16px, -4px) scale(0.85)',
          color: 'rgba(17, 17, 17, 0.7) !important',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <CaretDown size={24} />,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.Mui-disabled': {
            backgroundColor: palette.neutral[100],
          },
          '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
            backgroundColor: palette.neutral[100],
          },
        },
        input: {
          padding: '0px 8px !important',
        },
        option: {
          padding: '16px 0px !important',
          '&:not(:last-child)': {
            borderBottom: `1px solid ${palette.neutral[200]}`,
          },
        },
        listbox: {
          padding: '16px',
          '.MuiAutocomplete-option': {
            '&.Mui-focused': {
              backgroundColor: palette.neutral[100],
            },
            '&[aria-selected="true"]': {
              backgroundColor: palette.neutral[100],
            },
            '&.Mui-focused[aria-selected="true"]': {
              backgroundColor: palette.neutral[100],
            },
          },
          popupIndicator: {
            marginRight: 8,
            color: palette.neutral[700],
          },
          clearIndicator: {
            color: palette.neutral[700],
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'none',
          marginRight: 8,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          [defaultTheme.breakpoints.down('xs')]: {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.neutral[100],
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 28,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: (props: any):JSX.Element => <CaretDown size={16} {...props} />,
      },
      styleOverrides: {
        select: {
          '&:read-only': {
            backgroundColor: 'inherit !important',
            color: 'inherit !important',
          },
          '&:focus': {
            backgroundColor: 'transparent !important',
          },
        },
        icon: {
          color: palette.neutral[700],
          marginRight: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: palette.neutral[100],
            '&:hover': {
              backgroundColor: palette.neutral[100],
            },
            '&.Mui-focusVisible': {
              backgroundColor: palette.neutral[100],
            },
          },
          '&:hover': {
            backgroundColor: palette.neutral[100],
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
        checkedIcon: <CheckSquare size={24} />,
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiGrid2: {
      styleOverrides: {
        root: {
          '--Grid-nested-columnSpacing': 0,
          '--Grid-nested-rowSpacing': 0,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
  },
} as Theme);
